"use client";

import { cn } from "@/lib/helpers";
import React from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger";

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

export default function Button({
  variant = "primary",
  type = "button",
  loading = false,
  disabled,
  iconLeft,
  iconRight,
  children,
  className = "",
  ...props
}: ButtonProps) {
  const base = "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 font-medium transition-all duration-200 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed";

  const defaultColors: Record<ButtonVariant, string> = {
    primary: "bg-primary text-white hover:bg-primary-dark",
    secondary: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
    outline: "text-primary border border-current bg-transparent hover:bg-primary/10",
    ghost: "text-gray-700 bg-transparent hover:bg-gray-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };

  // Detect if className overrides bg or text colors
  const hasCustomColor = /bg-|text-|hover:/.test(className);

  const classes = cn(
    base,
    !hasCustomColor && defaultColors[variant], // only apply defaults if no color overrides
    className
  );

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={classes}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          ></path>
        </svg>
      )}
      {!loading && iconLeft && <span>{iconLeft}</span>}
      <span>{children}</span>
      {iconRight && <span>{iconRight}</span>}
    </button>
  );
}
